import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const VideoForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, post, put, loading } = useApi();
  
  const [formData, setFormData] = useState({
    uuid: '',
    deviceId: '',
    tripId: '',
    eventId: '',
    videoFileId: '',
    thumbnailFileId: '',
    timestamp: '',
    duration: '',
    encryptionKeyId: '',
    resolution: '',
    framerate: ''
  });

  const [devices, setDevices] = useState([]);
  const [trips, setTrips] = useState([]);
  const [events, setEvents] = useState([]);
  const [files, setFiles] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchAllData();
  }, [id]);

  const fetchAllData = async () => {
    setLoadingData(true);
    
    // Fetch devices
    const devicesRes = await get('/api/devices/filter?size=100');
    if (devicesRes.data) setDevices(devicesRes.data.content || []);

    // Fetch trips
    const tripsRes = await get('/api/trips/filter?size=100');
    if (tripsRes.data) setTrips(tripsRes.data.content || []);

    // Fetch events
    const eventsRes = await get('/api/events/filter?size=100');
    if (eventsRes.data) setEvents(eventsRes.data.content || []);

    // Fetch files
    const filesRes = await get('/api/files/filter?size=100');
    if (filesRes.data) setFiles(filesRes.data.content || []);

    // Fetch video data if editing
    if (id) {
      const videoRes = await get(`/api/videos/${id}`);
      if (videoRes.data) {
        setFormData({
          uuid: videoRes.data.uuid || '',
          deviceId: videoRes.data.deviceId || '',
          tripId: videoRes.data.tripId || '',
          eventId: videoRes.data.eventId || '',
          videoFileId: videoRes.data.videoFileId || '',
          thumbnailFileId: videoRes.data.thumbnailFileId || '',
          timestamp: videoRes.data.timestamp ? videoRes.data.timestamp.slice(0, 16) : '',
          duration: videoRes.data.duration || '',
          encryptionKeyId: videoRes.data.encryptionKeyId || '',
          resolution: videoRes.data.resolution || '',
          framerate: videoRes.data.framerate || ''
        });
      }
    }

    setLoadingData(false);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.deviceId) {
      newErrors.deviceId = 'Device is required';
    }
    if (!formData.timestamp) {
      newErrors.timestamp = 'Timestamp is required';
    }
    if (formData.duration && formData.duration < 0) {
      newErrors.duration = 'Duration cannot be negative';
    }
    if (formData.framerate && formData.framerate < 0) {
      newErrors.framerate = 'Framerate cannot be negative';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Convert numeric fields
    const submitData = {
      uuid: formData.uuid || null,
      deviceId: parseInt(formData.deviceId),
      tripId: formData.tripId ? parseInt(formData.tripId) : null,
      eventId: formData.eventId ? parseInt(formData.eventId) : null,
      videoFileId: formData.videoFileId ? parseInt(formData.videoFileId) : null,
      thumbnailFileId: formData.thumbnailFileId ? parseInt(formData.thumbnailFileId) : null,
      timestamp: formData.timestamp,
      duration: formData.duration ? parseInt(formData.duration) : null,
      encryptionKeyId: formData.encryptionKeyId || null,
      resolution: formData.resolution || null,
      framerate: formData.framerate ? parseInt(formData.framerate) : null
    };

    let response;
    if (id) {
      response = await put(`/api/videos/${id}`, submitData);
    } else {
      response = await post('/api/videos', submitData);
    }

    if (!response.error) {
      navigate('/videos/filter');
    }
  };

  if (loadingData || (loading && id)) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/videos/filter')}
          className="mr-4 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          {id ? 'Edit Video' : 'Add New Video'}
        </h1>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* UUID */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                UUID
              </label>
              <input
                type="text"
                name="uuid"
                value={formData.uuid}
                onChange={handleChange}
                className="input-field font-mono text-sm"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              />
            </div>

            {/* Device Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Device <span className="text-red-500">*</span>
              </label>
              <select
                name="deviceId"
                value={formData.deviceId}
                onChange={handleChange}
                className={`input-field ${errors.deviceId ? 'border-red-500' : ''}`}
              >
                <option value="">Select a device</option>
                {devices.map(device => (
                  <option key={device.id} value={device.id}>
                    Device #{device.id} - {device.brand} {device.devType}
                  </option>
                ))}
              </select>
              {errors.deviceId && (
                <p className="mt-1 text-sm text-red-600">{errors.deviceId}</p>
              )}
            </div>

            {/* Trip Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trip (Optional)
              </label>
              <select
                name="tripId"
                value={formData.tripId}
                onChange={handleChange}
                className="input-field"
              >
                <option value="">No trip</option>
                {trips.map(trip => (
                  <option key={trip.id} value={trip.id}>
                    Trip #{trip.id} - {trip.label || 'No label'}
                  </option>
                ))}
              </select>
            </div>

            {/* Event Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Event (Optional)
              </label>
              <select
                name="eventId"
                value={formData.eventId}
                onChange={handleChange}
                className="input-field"
              >
                <option value="">No event</option>
                {events.map(event => (
                  <option key={event.id} value={event.id}>
                    Event #{event.id} - {event.eventType?.description || 'Unknown'}
                  </option>
                ))}
              </select>
            </div>

            {/* Video File Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Video File (Optional)
              </label>
              <select
                name="videoFileId"
                value={formData.videoFileId}
                onChange={handleChange}
                className="input-field"
              >
                <option value="">No file</option>
                {files.map(file => (
                  <option key={file.id} value={file.id}>
                    File #{file.id} - {file.path?.split('/').pop() || 'Unknown'}
                  </option>
                ))}
              </select>
            </div>

            {/* Thumbnail File Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Thumbnail File (Optional)
              </label>
              <select
                name="thumbnailFileId"
                value={formData.thumbnailFileId}
                onChange={handleChange}
                className="input-field"
              >
                <option value="">No thumbnail</option>
                {files.map(file => (
                  <option key={file.id} value={file.id}>
                    File #{file.id} - {file.path?.split('/').pop() || 'Unknown'}
                  </option>
                ))}
              </select>
            </div>

            {/* Timestamp */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timestamp <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                name="timestamp"
                value={formData.timestamp}
                onChange={handleChange}
                className={`input-field ${errors.timestamp ? 'border-red-500' : ''}`}
              />
              {errors.timestamp && (
                <p className="mt-1 text-sm text-red-600">{errors.timestamp}</p>
              )}
            </div>

            {/* Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration (seconds)
              </label>
              <input
                type="number"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                className={`input-field ${errors.duration ? 'border-red-500' : ''}`}
                placeholder="e.g., 120"
              />
              {errors.duration && (
                <p className="mt-1 text-sm text-red-600">{errors.duration}</p>
              )}
            </div>

            {/* Encryption Key ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Encryption Key ID
              </label>
              <input
                type="text"
                name="encryptionKeyId"
                value={formData.encryptionKeyId}
                onChange={handleChange}
                className="input-field"
                placeholder="Encryption key ID"
              />
            </div>

            {/* Resolution */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resolution
              </label>
              <input
                type="text"
                name="resolution"
                value={formData.resolution}
                onChange={handleChange}
                className="input-field"
                placeholder="e.g., 1920x1080"
              />
            </div>

            {/* Framerate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Framerate (fps)
              </label>
              <input
                type="number"
                name="framerate"
                value={formData.framerate}
                onChange={handleChange}
                className={`input-field ${errors.framerate ? 'border-red-500' : ''}`}
                placeholder="e.g., 30"
              />
              {errors.framerate && (
                <p className="mt-1 text-sm text-red-600">{errors.framerate}</p>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/videos/filter')}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : id ? 'Update Video' : 'Create Video'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VideoForm;